// Cart Promo Code Web Component
class CartPromoCode extends HTMLElement {
  constructor() {
    super();
    this.toggleButton = this.querySelector('.cart-drawer__promo-code-toggle');
    this.form = this.querySelector('.cart-drawer__promo-code-form');
    this.submitButton = this.querySelector('.cart-promo-code-field__submit');
    this.input = this.querySelector('input[type="text"]');
    this.hiddenInput = this.querySelector('input[name="discount"]');
    this.errorBanner = this.querySelector('cart-discount-banner');
    this.successBanner = this.querySelector('.cart-drawer__promo-code-success');
    
    // Store bound handlers so we can remove them later
    this._boundHandleToggle = this.handleToggle.bind(this);
    this._boundHandleSubmit = this.handleSubmit.bind(this);
    this._boundHandleCartChange = this.handleCartChange.bind(this);
    this._boundHandleKeypress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSubmit();
      }
    };
    this._boundRefreshHandler = null; // Will be set in init
    
    this.init();
  }

  init() {
    this.attachEventListeners();

    // Listen for cart changes to update visibility (only add once per component instance)
    document.removeEventListener('cart:change', this._boundHandleCartChange);
    document.addEventListener('cart:change', this._boundHandleCartChange);
    
    // Listen for cart drawer refresh completion
    this._boundRefreshHandler = async () => {
      // Check sessionStorage for flags (persists across DOM replacements)
      const shouldKeepOpen = sessionStorage.getItem('cart-promo-code-keep-open') === 'true';
      const shouldShowSuccess = sessionStorage.getItem('cart-promo-code-show-success') === 'true';
      
      // Re-query elements after DOM replacement
      this.form = this.querySelector('.cart-drawer__promo-code-form');
      this.toggleButton = this.querySelector('.cart-drawer__promo-code-toggle');
      this.successBanner = this.querySelector('.cart-drawer__promo-code-success');
      this.errorBanner = this.querySelector('cart-discount-banner');
      this.submitButton = this.querySelector('.cart-promo-code-field__submit');
      this.input = this.querySelector('input[type="text"]');
      this.hiddenInput = this.querySelector('input[name="discount"]');
      
      // Show success message after cart has rehydrated
      if (shouldShowSuccess && this.successBanner) {
        // Wait for next frame to ensure cart is fully updated and DOM is stable
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.showSuccess();
            sessionStorage.removeItem('cart-promo-code-show-success');
          });
        });
      }
      
      // Re-attach event listeners (will remove old ones first)
      this.attachEventListeners();
      
      // Set form visibility immediately if flag is set (before async operations)
      if (shouldKeepOpen && this.form) {
        const keepFormOpen = () => {
          if (this.form && shouldKeepOpen) {
            this.form.hidden = false;
            this.form.removeAttribute('hidden');
            this.form.style.display = '';
          }
        };
        
        // Set immediately
        keepFormOpen();
        
        // Set up MutationObserver to keep form open if something tries to hide it
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
              keepFormOpen();
            }
          });
        });
        
        observer.observe(this.form, {
          attributes: true,
          attributeFilter: ['hidden'],
          attributeOldValue: true
        });
        
        // Also use setInterval as a fallback to continuously check and force it open
        const intervalId = setInterval(() => {
          if (this.form && this.form.hidden && shouldKeepOpen) {
            keepFormOpen();
          }
        }, 50); // Check every 50ms
        
        // Clean up after 3 seconds
        setTimeout(() => {
          observer.disconnect();
          clearInterval(intervalId);
        }, 3000);
        
        if (this.toggleButton) {
          this.toggleButton.hidden = true;
        }
      }
      
      // Use requestAnimationFrame to ensure we run after any other code that might reset the form
      requestAnimationFrame(async () => {
        // Check cart data directly to see if discounts are applied (more reliable than DOM query)
        try {
          const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
          const cart = await cartResponse.json();
          const hasDiscounts = cart.discount_codes?.some(code => code.applicable === true) || false;
          
          // Keep form open if discounts are applied OR if we set the flag
          if ((hasDiscounts || shouldKeepOpen) && this.form) {
            this.form.hidden = false;
            this.form.removeAttribute('hidden');
            if (this.toggleButton) {
              this.toggleButton.hidden = true;
            }
            // Clear flag after handling (but keep it a bit longer to ensure form stays open)
            setTimeout(() => {
              sessionStorage.removeItem('cart-promo-code-keep-open');
            }, 1000);
          }
        } catch (error) {
          console.error('Error checking cart after refresh:', error);
        }
      });
    };
    
    // Remove old listener before adding new one
    if (this._boundRefreshHandler) {
      document.removeEventListener('cart-drawer:refreshed', this._boundRefreshHandler);
    }
    document.addEventListener('cart-drawer:refreshed', this._boundRefreshHandler);
  }

  attachEventListeners() {
    // Remove old listeners before adding new ones to prevent duplicates
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this._boundHandleToggle);
      this.toggleButton.addEventListener('click', this._boundHandleToggle);
    }

    if (this.submitButton) {
      this.submitButton.removeEventListener('click', this._boundHandleSubmit);
      this.submitButton.addEventListener('click', this._boundHandleSubmit);
    }

    if (this.input) {
      this.input.removeEventListener('keypress', this._boundHandleKeypress);
      this.input.addEventListener('keypress', this._boundHandleKeypress);
    }
  }

  handleToggle() {
    // Re-query elements to ensure we have current references
    this.toggleButton = this.querySelector('.cart-drawer__promo-code-toggle');
    this.form = this.querySelector('.cart-drawer__promo-code-form');
    
    if (!this.toggleButton || !this.form) {
      return;
    }
    
    const isExpanded = this.toggleButton.getAttribute('aria-expanded') === 'true';
    
    // Hide success message when manually toggling
    this.hideSuccess();
    
    this.form.hidden = isExpanded;
    this.toggleButton.setAttribute('aria-expanded', !isExpanded);
    
    // Focus the input when expanded
    if (!isExpanded && this.input) {
      setTimeout(() => {
        this.input.focus();
      }, 100);
    }
  }

  async getDiscountCodes() {
    try {
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      return cart.discount_codes?.filter(discount => discount.applicable).map(discount => discount.code.toLowerCase()) || [];
    } catch (error) {
      console.error('Error fetching cart:', error);
      return [];
    }
  }

  async handleSubmit() {
    const code = this.input.value.trim();
    
    if (!code) {
      this.showError();
      return;
    }

    this.setLoading(true);
    this.hideError();
    this.hideSuccess();

    try {
      const discountCodes = await this.getDiscountCodes();
      const discount = (discountCodes.length > 0 ? discountCodes.join(',') + ',' : '') + code;
      
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({ discount })
      });

      this.setLoading(false);

      if (!response.ok) {
        this.showError();
        return;
      }

      const responseJson = await response.json();
      
      // Check if the discount code was successfully applied
      const appliedCode = responseJson.discount_codes?.find(obj => 
        obj.code.toLowerCase() === code.toLowerCase() && obj.applicable === true
      );
      
      // Check if any discount code failed to apply
      const hasInvalidCode = responseJson.discount_codes?.some(obj => obj.applicable === false);
      
      if (hasInvalidCode && !appliedCode) {
        this.showError();
        return;
      }

      // Success - discount code was applied
      if (appliedCode) {
        this.input.value = '';
        this.hideError();
        // Don't show success yet - wait for cart to rehydrate
        
        // Hide toggle button (success message will show after rehydration)
        if (this.toggleButton) {
          this.toggleButton.hidden = true;
        }
        
        // Store flags to show success and keep form open after refresh
        sessionStorage.setItem('cart-promo-code-keep-open', 'true');
        sessionStorage.setItem('cart-promo-code-show-success', 'true');
        
        // Keep form open (don't hide it)
        if (this.form) {
          this.form.hidden = false;
          this.form.removeAttribute('hidden');
        }
        
        // Dispatch cart refresh event
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        
        // Update hidden input
        if (this.hiddenInput) {
          const newCodes = await this.getDiscountCodes();
          this.hiddenInput.value = newCodes.join(',');
        }
      } else {
        // No discount was applied (shouldn't happen, but handle it)
        this.showError();
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      this.setLoading(false);
      this.showError();
    }
  }

  setLoading(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading;
    }
    if (this.input) {
      this.input.disabled = loading;
    }
  }

  showError() {
    if (this.errorBanner) {
      this.errorBanner.hidden = false;
    }
  }

  hideError() {
    if (this.errorBanner) {
      this.errorBanner.hidden = true;
    }
  }

  showSuccess() {
    if (this.successBanner) {
      this.successBanner.hidden = false;
      // Hide success message after 3 seconds
      setTimeout(() => {
        this.hideSuccess();
        // After success message hides, show toggle button again if no discounts
        const hasDiscounts = document.querySelectorAll('.discount-badge').length > 0;
        if (!hasDiscounts && this.toggleButton) {
          this.toggleButton.hidden = false;
        }
      }, 3000);
    }
  }

  hideSuccess() {
    if (this.successBanner) {
      this.successBanner.hidden = true;
    }
  }

  handleCartChange() {
    const hasDiscounts = document.querySelectorAll('.discount-badge').length > 0;
    
    // Check if we should show success message after cart change (cart rehydration)
    const shouldShowSuccess = sessionStorage.getItem('cart-promo-code-show-success') === 'true';
    if (shouldShowSuccess && this.successBanner && hasDiscounts) {
      // Cart has changed and discounts are visible - show success now
      requestAnimationFrame(() => {
        this.showSuccess();
        sessionStorage.removeItem('cart-promo-code-show-success');
      });
    }
    
    if (this.form) {
      if (hasDiscounts) {
        // Keep form open when discounts are applied
        this.form.hidden = false;
        this.hideError();
        // Don't hide success message here - let it auto-hide after timeout
      } else {
        // When no discounts, sync form state with aria-expanded attribute
        // But DON'T override if user manually closed it - only sync if state is inconsistent
        if (this.toggleButton) {
          const ariaExpanded = this.toggleButton.getAttribute('aria-expanded') === 'true';
          const formShouldBeHidden = !ariaExpanded;
          // Only sync if there's a mismatch (don't force state changes)
          if (this.form.hidden !== formShouldBeHidden) {
            this.form.hidden = formShouldBeHidden;
          }
        }
      }
    }
    
    if (this.toggleButton) {
      if (hasDiscounts) {
        // Hide toggle button when discounts are applied (success message replaces it)
        this.toggleButton.hidden = true;
        this.toggleButton.setAttribute('aria-expanded', 'true');
      } else {
        // Show toggle button if no discounts
        this.toggleButton.hidden = false;
        this.hideSuccess();
      }
    }
  }
}

if (!window.customElements.get('cart-promo-code')) {
  window.customElements.define('cart-promo-code', CartPromoCode);
}
